import React from 'react';

/**
 * Table to display 15 days of future temperature data.
 * Intelligently shows HIGH or LOW temps based on which extreme matters more (heating vs cooling).
 * - LOW temps (heating days) highlighted in royal blue
 * - HIGH temps (cooling days) highlighted in crimson
 * @param {Array} data - [{ date: 'YYYY-MM-DD', hour: string, temperature: number|null, type: 'low'|'high' }]
 */
function FutureTemperatureTable({ data = [] }) {
  if (!data.length) return <p>No future temperature data available.</p>;

  const COLD_THRESHOLD = 50;
  const COLORS = {
    low: '#2563EB',   // Royal blue for heating days (lows)
    high: '#DC2626'   // Crimson for cooling days (highs)
  };

  // Simple rule: if ANY temp is below 50°F, we're in "cold mode" - show lows
  const temps = data.map(d => d.temperature).filter(v => typeof v === 'number');
  const minTemp = temps.length ? Math.min(...temps) : null;
  // eslint-disable-next-line no-unused-vars
  const maxTemp = temps.length ? Math.max(...temps) : null;
  const isColdMode = minTemp !== null && minTemp < COLD_THRESHOLD;

  // Find the extreme to highlight
  // Cold mode: highlight the LOWEST (most heating demand)
  // Hot mode: highlight the HIGHEST (most cooling demand)
  let extremeIdx = -1;
  let extremeValue = isColdMode ? Infinity : -Infinity;

  data.forEach((row, idx) => {
    if (typeof row.temperature !== 'number') return;
    if (isColdMode) {
      if (row.temperature < extremeValue) {
        extremeValue = row.temperature;
        extremeIdx = idx;
      }
    } else {
      if (row.temperature > extremeValue) {
        extremeValue = row.temperature;
        extremeIdx = idx;
      }
    }
  });

  const highlightColor = isColdMode ? COLORS.low : COLORS.high;

  return (
    <div style={{ marginTop: 32 }}>
      <h2>15-Day Future Temperatures</h2>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ccc', padding: '4px' }}>Date</th>
            <th style={{ border: '1px solid #ccc', padding: '4px' }}>Hour</th>
            <th style={{ border: '1px solid #ccc', padding: '4px' }}>Temperature (°F)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isExtreme = idx === extremeIdx;
            return (
              <tr key={row.date}>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>{row.date}</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>{row.hour}</td>
                <td
                  style={{
                    border: '1px solid #ccc',
                    padding: '4px',
                    background: isExtreme ? highlightColor : undefined,
                    color: isExtreme ? 'white' : undefined,
                    fontWeight: isExtreme ? 'bold' : undefined,
                  }}
                >
                  {typeof row.temperature === 'number' ? row.temperature.toFixed(1) : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: '#888' }}>
        {isColdMode
          ? 'Showing daily LOWS (heating demand). Coldest day highlighted in blue.'
          : 'Showing daily HIGHS (cooling demand). Hottest day highlighted in red.'}
      </div>
    </div>
  );
}

export default FutureTemperatureTable;
